import { useMemo } from 'react'

import {
  useFilters, useModals, useSetState, useShowModal,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import { ArtworkDownloadContext, BuildingArtworkModal } from '@domain-group/artwork-builder'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useStatus from '@hooks/useStatus'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { ActivityModel, OrderItemModel, OrderModel } from '@models/types'

import EditOrderModal from '@modals/EditOrderModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageBrandOrderArtworkModal from '@modals/ManageBrandOrderArtworkModal'
import ManageProviderOrderItemModal from '@modals/ManageProviderOrderItemModal'

import PageContent from './components/PageContent'

const defaultState = {
  showEditOrderModal: false,
  showManageActivityModal: false,
  showManageBrandOrderArtworkModal: false,
  showManageProviderOrderItemModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

// eslint-disable-next-line no-undef
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    EditOrderModal: {
      closeModal: () => setState({ showEditOrderModal: false }),
      updateOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageBrandOrderArtworkModal: {
      closeModal: () => setState({ showManageBrandOrderArtworkModal: false }),
    },
    ManageProviderOrderItemModal: {
      closeModal: () => setState({ showManageProviderOrderItemModal: false }),
      updateProviderOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const currentUserPayload = useCurrentUser()
  const { isBrandUser } = currentUserPayload

  const [state, setState] = useSetState(defaultState)
  const {
    showEditOrderModal,
    showManageActivityModal,
    showManageBrandOrderArtworkModal,
    showManageProviderOrderItemModal,
  } = state

  const { status } = useStatus({}, { defaultStatusKey: 'awaiting_review' })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  // @ts-expect-error TS2339
  } = useFilters({ defaultFilters: { status_id: isBrandUser ? status?.id : 'current' } })

  const modalContext = useModals({ setState })

  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showEditOrderModal: (payload: Partial<OrderModel>) => {
        setModalData('EditOrderModal', payload)
        setState({ showEditOrderModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageBrandOrderArtworkModal: (payload: { orderItem: Partial<OrderItemModel> }) => {
        setModalData('ManageBrandOrderArtworkModal', payload)
        setState({ showManageBrandOrderArtworkModal: true })
      },
      showManageProviderOrderItemModal: (payload: { publicationBooking: OrderItemModel }) => {
        setModalData('ManageProviderOrderItemModal', payload)
        setState({ showManageProviderOrderItemModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt])

  useShowModal({
    modalKey: 'EditOrderModal',
    options: {
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent currentUserPayload={currentUserPayload} pageFilters={pageFilters} />

        <BuildingArtworkModal context={ArtworkDownloadContext} />

        <EditOrderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditOrderModal', setState)}
          showModal={showEditOrderModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageBrandOrderArtworkModal
          callbacks={callbacks('ManageBrandOrderArtworkModal', setState)}
          showModal={showManageBrandOrderArtworkModal}
        />

        <ManageProviderOrderItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageProviderOrderItemModal', setState)}
          showModal={showManageProviderOrderItemModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
